import React from 'react';
import { stills } from '../data/projects';
import type { VideoFormat } from '../types/portal';
import { EMBED_ERROR, toDrivePreviewUrl } from '../utils/drive';
interface VideoStageProps {
  driveUrl: string;
  label: string;
  runtime: string;
  /** Framing set by Portal. Absent means landscape, so nothing existing breaks. */
  format?: VideoFormat;
}

/**
 * The video review stage.
 *
 * A Google Drive link is embedded directly — Drive's own player supplies
 * playback, scrubbing and volume, so nothing here fakes transport controls
 * against a runtime that was never real. No link yet, and a link Drive can't
 * embed, are two different states and are said plainly rather than both
 * falling into a player that looks like it works.
 */
export function VideoStage({
  driveUrl,
  label,
  runtime,
  format
}: VideoStageProps) {
  /* The wrapper alone sets the ratio — Drive's own content is never stretched. */
  const vertical = format === '9:16 Vertical';
  const embedUrl = toDrivePreviewUrl(driveUrl);
  const linkProvided = Boolean(driveUrl.trim());
  /** Only the link itself can be judged — Drive is cross-origin and opaque. */
  const showError = linkProvided && !embedUrl;
  return <figure className={vertical ? 'm-0 mx-auto w-full max-w-[440px]' : 'm-0'} data-id="element-1848" data-mp="31lsvzx001bbq">
      <div className={['relative w-full overflow-hidden bg-ink', vertical ? 'aspect-[9/16]' : 'aspect-video'].join(' ')} data-id="element-1849" data-mp="31jq8pkq8dwap">
        {embedUrl ? <iframe title={label} src={embedUrl} allow="autoplay; fullscreen" allowFullScreen className="h-full w-full border-0" data-id="element-1850" data-mp="29siqsvan88kl" /> : showError ? <div className="flex h-full w-full flex-col items-start justify-end p-6 sm:p-8" data-id="element-1851" data-mp="2dwqgovsu7uke">
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale" data-id="element-1852" data-mp="1q6nsiqs1n8r2" />
            <div className="absolute inset-0 bg-ink/70" data-id="element-1853" data-mp="1m37upr48n2cs" />
            <div className="relative" data-id="element-1854" data-mp="3l9118mxo1xy5">
              <p className="label m-0 text-paper/50" data-id="element-1855" data-mp="3l7zjjzm5ad7g">Preview Unavailable</p>
              <p className="mt-4 max-w-md font-display text-xl leading-snug text-paper sm:text-2xl" data-id="element-1856" data-mp="3q7kn6nc9f9qi">
                {EMBED_ERROR}
              </p>
              <a href={driveUrl} target="_blank" rel="noreferrer" className="label mt-7 inline-block border border-paper/50 px-5 py-3 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-1857" data-mp="3tayed4f3zp7">
                Open in Google Drive
              </a>
            </div>
          </div> : <div className="flex h-full w-full flex-col items-start justify-end p-6 sm:p-8" data-id="element-1858" data-mp="3h87brp4ksb35">
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale" data-id="element-1859" data-mp="rj5agsgwxk8e" />
            <div className="absolute inset-0 bg-ink/70" data-id="element-1860" data-mp="wkchuqf58hej" />
            <div className="relative" data-id="element-1861" data-mp="2gz8x36cbtq6o">
              <p className="label m-0 text-paper/50" data-id="element-1862" data-mp="2h2d8oos4fo7d">Awaiting Video</p>
              <p className="mt-4 max-w-md font-display text-xl leading-snug text-paper sm:text-2xl" data-id="element-1863" data-mp="2c2iikng672nd">
                The Google Drive preview link hasn't been added yet.
              </p>
            </div>
          </div>}
      </div>

      <figcaption className="mt-3 flex flex-wrap items-center justify-between gap-3" data-id="element-1864" data-mp="229jrmyx7b00l">
        <span className="label text-mute" data-id="element-1865" data-mp="1xa6xdt402qnz">
          {label}
          {runtime ? ` · ${runtime}` : ''}
        </span>
        {embedUrl ? <a href={driveUrl} target="_blank" rel="noreferrer" className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-1866" data-mp="1oatl6sohrnzk">
            Open in Google Drive
          </a> : <span className="label text-mute" data-id="element-1867" data-mp="3ina00hvcsag1">
            {showError ? 'Check the Drive link' : 'Awaiting Drive preview link'}
          </span>}
      </figcaption>
    </figure>;
}